const ACTIVE_CLASS = 'b-hero-image--is-active';
const ENTERING_CLASS = 'b-hero-image--is-entering';
const EXITING_CLASS = 'b-hero-image--is-exiting';
const ROTATING_CLASS = 'b-hero-images--is-rotating';
const MIN_ROTATION_INTERVAL_SECONDS = 3;
const MAX_ROTATION_INTERVAL_SECONDS = 8;
const DEFAULT_ROTATION_INTERVAL_SECONDS = 5;
const ROTATION_INTERVAL_MS = DEFAULT_ROTATION_INTERVAL_SECONDS * 1000;

/**
 * Resolve rotation interval for block from data attribute.
 *
 * @param {HTMLElement} block Hero images block node.
 * @return {number} Rotation interval in milliseconds.
 */
const getRotationIntervalMs = ( block ) => {

	const intervalSecondsRaw = block.dataset.heroImagesIntervalSeconds;
	const intervalSeconds = Number.parseFloat( intervalSecondsRaw || '' );

	if (
		Number.isFinite( intervalSeconds ) &&
		intervalSeconds >= MIN_ROTATION_INTERVAL_SECONDS &&
		intervalSeconds <= MAX_ROTATION_INTERVAL_SECONDS
	) {

		return intervalSeconds * 1000;

	}

	return ROTATION_INTERVAL_MS;

};

/**
 * Mark one hero image as active.
 *
 * @param {HTMLElement[]} images Hero image nodes.
 * @param {number} activeIndex Active image index.
 * @return {void}
 */
const setActiveImage = ( images, activeIndex ) => {

	images.forEach( ( image, index ) => {

		const isActive = index === activeIndex;

		image.classList.remove( ENTERING_CLASS, EXITING_CLASS );
		image.classList.toggle( ACTIVE_CLASS, isActive );
		image.setAttribute( 'aria-hidden', isActive ? 'false' : 'true' );

	} );

};

/**
 * Animate transition between currently active and next image.
 *
 * @param {HTMLElement[]} images Hero image nodes.
 * @param {number} activeIndex Current active index.
 * @param {number} nextIndex Next active index.
 * @param {Function} onComplete Callback run after transition completes.
 * @return {void}
 */
const transitionToImage = async ( images, activeIndex, nextIndex, onComplete ) => {

	const activeImage = images[activeIndex];
	const nextImage = images[nextIndex];

	if ( ! activeImage || ! nextImage || activeImage === nextImage ) {

		onComplete();
		return;

	}

	activeImage.classList.add( EXITING_CLASS );
	nextImage.classList.add( ENTERING_CLASS );

	activeImage.setAttribute( 'aria-hidden', 'true' );
	nextImage.setAttribute( 'aria-hidden', 'false' );

	/**
	 * Finalize slide state after the incoming animation.
	 *
	 * @param {AnimationEvent} event Animation event.
	 * @return {void}
	 */
	const handleAnimationEnd = ( event ) => {

		if ( event.target !== nextImage ) {

			return;

		}

		nextImage.removeEventListener( 'animationend', handleAnimationEnd );

		activeImage.classList.remove( ACTIVE_CLASS, EXITING_CLASS );
		nextImage.classList.remove( ENTERING_CLASS );
		nextImage.classList.add( ACTIVE_CLASS );

		onComplete();

	};

	nextImage.addEventListener( 'animationend', handleAnimationEnd );

};

/**
 * Initialize image rotation for a single hero images block.
 *
 * @param {HTMLElement} block Hero images block node.
 * @return {void}
 */
const initHeroImages = ( block ) => {

	const images = Array.from( block.querySelectorAll( '[data-hero-image]' ) );

	if ( images.length <= 1 ) {

		block.classList.remove( ROTATING_CLASS );

		images.forEach( ( image ) => {

			image.classList.remove( ACTIVE_CLASS, ENTERING_CLASS, EXITING_CLASS );
			image.setAttribute( 'aria-hidden', 'false' );

		} );

		return;

	}

	block.classList.add( ROTATING_CLASS );

	let activeIndex = 0;
	let isAnimating = false;
	const rotationIntervalMs = getRotationIntervalMs( block );

	setActiveImage( images, activeIndex );

	/**
	 * Schedule next slide transition.
	 *
	 * @return {void}
	 */
	const scheduleNextTransition = () => {

		window.setTimeout( () => {

			if ( isAnimating ) {

				scheduleNextTransition();
				return;

			}

			const nextIndex = ( activeIndex + 1 ) % images.length;

			isAnimating = true;

			transitionToImage( images, activeIndex, nextIndex, () => {

				activeIndex = nextIndex;
				isAnimating = false;

				scheduleNextTransition();

			} );

		}, rotationIntervalMs );

	};

	scheduleNextTransition();

};

/**
 * Initialize all hero images blocks on page.
 *
 * @return {void}
 */
const init = () => {

	const blocks = document.querySelectorAll( '[data-hero-images]' );

	if ( ! blocks.length ) {

		return;

	}

	blocks.forEach( ( block ) => initHeroImages( block ) );

};

const INTERACTION_EVENTS = [ 'mousemove', 'touchstart', 'scroll', 'keydown' ];

/**
 * Defer rotation start until the first real user interaction, so tools like
 * PageSpeed Insights/Lighthouse — which never fire these events — don't
 * measure the extra image decode/paint caused by the animation kicking in
 * right after initial page load.
 *
 * @return {void}
 */
const initOnInteraction = () => {

	let hasInitialized = false;

	/**
	 * Run init() once, then remove all listeners.
	 *
	 * @return {void}
	 */
	const runOnce = () => {

		if ( hasInitialized ) {

			return;

		}

		hasInitialized = true;

		INTERACTION_EVENTS.forEach( ( eventName ) => {

			window.removeEventListener( eventName, runOnce );

		} );

		init();

	};

	INTERACTION_EVENTS.forEach( ( eventName ) => {

		window.addEventListener( eventName, runOnce, { passive: true } );

	} );

};

if ( document.readyState === 'complete' ) {

	initOnInteraction();

} else {

	window.addEventListener( 'load', initOnInteraction );

}
